import React from 'react';
import { useState } from 'react';
import { ReportContentModal } from '../common/ReportContentModal';
import { buildAiSnapshot } from '../../services/reportApi';
import ChatComponentRenderer from './ChatComponentRenderer';
import ChatSoftGate from './ChatSoftGate';
import { type UseChatReturn } from '../../hooks/useChat';
import { useSubscription } from '../../hooks/useSubscription';
import './ChatOverlay.css';

interface ChatOverlayProps {
  chat: UseChatReturn;
  onNavigate?: (path: string) => void;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({ chat, onNavigate }) => {
  const { handleUpgrade } = useSubscription();
  // Which assistant turn is being reported, if any.
  const [reportingMessageId, setReportingMessageId] = useState<string | null>(null);
  const {
    messages,
    isChatExpanded,
    isChatClosing,
    inputValue,
    isSending,
    softGate,
    messagesEndRef,
    inputRef,
    chatPanelRef,
    setInputValue,
    handleCloseChat,
    handleConfirmAction,
    handleSelectOption,
    handleViewItinerary,
    handleSubmit,
    handleQuickAction,
  } = chat;

  // The reported turn's own text. An id alone points at nothing an operator
  // can find, because chat ids are minted client-side and reissued on reload.
  const reportedMessage = messages.find((m) => String(m.id) === reportingMessageId);

  return (
    <div className={`home-chat-overlay ${isChatExpanded ? 'expanded' : ''} ${isChatClosing ? 'closing' : ''}`}>
      <div className="home-chat-panel" ref={chatPanelRef}>
        {/* Chat Header */}
        <div className="home-chat-header">
          <div className="home-chat-header-left">
            <div className="home-chat-avatar">
              <span>V</span>
            </div>
            <div className="home-chat-header-text">
              <span className="home-chat-title">VizDay</span>
              <span className="home-chat-subtitle">Let's plan something amazing</span>
            </div>
          </div>
          <button className="home-chat-close-btn" onClick={handleCloseChat}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Chat Messages */}
        <div className="home-chat-messages">
          {messages.map(message => (
            <div
              key={message.id}
              className={`chat-message ${message.role} ${message.isProactive ? 'proactive' : ''}`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar">V</div>
              )}
              <div className="message-bubble">
                <div className="message-text">{message.content}</div>
                {message.components?.map((component, idx) => (
                  <ChatComponentRenderer
                    key={idx}
                    component={component}
                    onConfirmAction={handleConfirmAction}
                    onSelectOption={handleSelectOption}
                    onViewItinerary={handleViewItinerary}
                  />
                ))}
                {/* Flagging AI output. Google's AI-Generated Content policy
                    wants this reachable from the response itself, not buried
                    in settings. Assistant turns only — reporting your own
                    message back to us is meaningless. */}
                {message.role === 'assistant' && !message.isProactive && (
                  <button
                    type="button"
                    className="message-report-btn"
                    onClick={() => setReportingMessageId(String(message.id))}
                    aria-label="Report this response"
                  >
                    Report
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Quick actions when only proactive greeting exists */}
          {messages.length <= 1 && (
            <div className="quick-actions-section">
              <div className="quick-actions">
                <button
                  className="quick-action-btn"
                  onClick={() => handleQuickAction("What's on my calendar this week?")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  What's on this week?
                </button>
                <button
                  className="quick-action-btn"
                  onClick={() => handleQuickAction("I need to add an event")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="16"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                  Add an event
                </button>
                <button
                  className="quick-action-btn"
                  onClick={() => handleQuickAction("Help me plan a trip")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  Plan a trip
                </button>
                <button
                  className="quick-action-btn"
                  onClick={() => handleQuickAction("What destinations are trending right now? Any good deals?")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  Trending destinations
                </button>
              </div>
            </div>
          )}

          {isSending && (
            <div className="chat-message assistant">
              <div className="message-avatar">V</div>
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          {softGate.active && (
            <ChatSoftGate
              gateType={softGate.type}
              onUpgrade={() => softGate.type === 'upgrade' ? handleUpgrade() : onNavigate?.('/login')}
              onDismiss={handleCloseChat}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input */}
        <form className="chat-input-area" onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            rows={2}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder={softGate.active ? 'Upgrade to continue chatting' : 'Ask VizDay anything...'}
            disabled={isSending || softGate.active}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isSending || softGate.active}
            className="send-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </form>
      </div>

      <ReportContentModal
        isOpen={reportingMessageId !== null}
        onClose={() => setReportingMessageId(null)}
        targetType="ai_message"
        targetId={reportingMessageId ?? ''}
        targetLabel="this AI response"
        context="home chat"
        contentSnapshot={buildAiSnapshot(
          reportedMessage?.content ?? '',
          reportedMessage?.components,
        )}
      />
    </div>
  );
};

export default ChatOverlay;
